/**
 * Pure coverage-derivation helpers.
 *
 * Coverage is the home metric and the serving gate is editable, so the state→gate
 * mapping and the matrix roll-up are factored out as pure functions — testable without
 * a renderer, and the single source of truth for both the Rule store matrix and the
 * Coverage console. No clock, no I/O.
 */
import type { CoverageEntry, GateValue } from './types.js';

export interface CoverageRollup {
  total: number;
  covered: number;
  thin: number;
  empty: number;
  /** Percentage of cells that are covered (rounded). */
  pct: number;
}

export function rollupCoverage(cells: CoverageEntry[]): CoverageRollup {
  const total = cells.length;
  const covered = cells.filter((c) => c.state === 'covered').length;
  const thin = cells.filter((c) => c.state === 'thin').length;
  const empty = cells.filter((c) => c.state === 'empty').length;
  const pct = total ? Math.round((covered / total) * 100) : 0;
  return { total, covered, thin, empty, pct };
}

export type DerivedGate = 'answer' | 'abstain' | 'no-cov';

/**
 * The serving gate as the Coverage console derives it from the editable thresholds:
 * at/above coveredMin a cell answers, at/above thinMin it abstains, below thinMin it
 * holds nothing (no-coverage).
 */
export function deriveGate(ruleCount: number, coveredMin: number, thinMin: number): DerivedGate {
  if (ruleCount >= coveredMin) return 'answer';
  if (ruleCount >= thinMin) return 'abstain';
  return 'no-cov';
}

/** A cell "flips" when it answers today but would drop below the new covered threshold. */
export function gateFlips(cells: CoverageEntry[], coveredMin: number): number {
  return cells.filter((c) => c.gate === 'answer' && c.rules < coveredMin).length;
}

export interface GateCounts {
  answering: number;
  abstaining: number;
  noCoverage: number;
}

export function gateCounts(cells: CoverageEntry[], coveredMin: number, thinMin: number): GateCounts {
  let answering = 0;
  let abstaining = 0;
  let noCoverage = 0;
  for (const c of cells) {
    const g = deriveGate(c.rules, coveredMin, thinMin);
    if (g === 'answer') answering++;
    else if (g === 'abstain') abstaining++;
    else noCoverage++;
  }
  return { answering, abstaining, noCoverage };
}

/** What a query at a cell returns, in the edge simulator. */
export function cellOutcome(state: CoverageEntry['state']): 'answer' | 'abstain' | 'nocov' {
  return state === 'covered' ? 'answer' : state === 'thin' ? 'abstain' : 'nocov';
}

/** Compose a coverage map key from jurisdiction + domain. */
export function cellKey(juris: string, domain: string): string {
  return `${juris}:${domain}`;
}

export function splitCellKey(key: string): { juris: string; domain: string } {
  const [juris = '', domain = ''] = key.split(':');
  return { juris, domain };
}

/** The matrix legend gate, fixed by the cell's coverage state (used by the read-only matrix). */
export function gateForState(state: CoverageEntry['state']): GateValue {
  return state === 'covered' ? 'answer' : 'abstain';
}
